import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { Global } from '../global';
import { Client } from '../models/client';
import { Family } from '../models/family';
import { HSABankRequestObject } from '../models/hsabank-request-object';
import { Spouse } from '../models/spouse';

@Injectable({
  providedIn: 'root'
})
export class FormService {
  private requestObjectSource: Subject<FormGroup> = new Subject();
  requestObject: Observable<FormGroup> = this.requestObjectSource.asObservable();
  requestObjectForm: FormGroup;

  expenseObject: any = {};
  hasDownloadedPDF: boolean = false;

  private clientObjectSource: Subject<FormGroup> = new Subject();
  clientObject: Observable<FormGroup> = this.clientObjectSource.asObservable();
  clientObjectForm: FormGroup;

  private spouseObjectSource: Subject<FormGroup> = new Subject();
  spouseObject: Observable<FormGroup> = this.spouseObjectSource.asObservable();
  spouseObjectForm: FormGroup;

  private familyObjectSource: Subject<FormGroup> = new Subject();
  familyObject: Observable<FormGroup> = this.familyObjectSource.asObservable();
  familyObjectForm: FormGroup;

  private preRetirementObjectSource: Subject<FormGroup> = new Subject();
  preRetirementObject: Observable<FormGroup> = this.preRetirementObjectSource.asObservable();
  preRetirementForm: FormGroup;

  private inRetirementObjectSource: Subject<FormGroup> = new Subject();
  inRetirementObject: Observable<FormGroup> = this.inRetirementObjectSource.asObservable();
  inRetirementForm: FormGroup;

  private clientHSADetailsSource: Subject<FormGroup> = new Subject();
  clientHSADetailsObject: Observable<FormGroup> = this.clientHSADetailsSource.asObservable();
  clientHSADetailsForm: FormGroup;

  private resultsResponseSource: Subject<any> = new Subject();
  resultsResponseObject: Observable<any> = this.resultsResponseSource.asObservable();
  resultsResponse: any;

  private resultsSource: Subject<any> = new Subject();
  resultsObject: Observable<any> = this.resultsSource.asObservable();
  resultsForm: FormGroup;

  private userObjectSource: Subject<FormGroup> = new Subject();
  userObject: Observable<FormGroup> = this.userObjectSource.asObservable();
  userObjectForm: FormGroup;

  constructor(private formBuilder: FormBuilder, public global: Global) {
    this.requestObjectForm = this.formBuilder.group({
      planForYear: [null]
    });

    this.familyObjectForm = this.formBuilder.group({
      spouse: [null],
      dependents: [],
      dependentsCount: [null],
      dependentsCutOffAge: [null],
      included: [null],
      hasOverrideCosts: false,
      isDependentsInsert: [null]
    });

    this.spouseObjectForm = this.formBuilder.group({
      currentAge: [null, [Validators.min(18), Validators.max(70), Validators.pattern(/^[0-9]\d*$/)]],
      healthConditions: [[0]],
      gender: ['', Validators.required],
      lifeExpectancy: [0],
      retirementAge: [null, [Validators.min(18), Validators.max(75), Validators.pattern(/^[0-9]\d*$/)]]
    });

    this.clientObjectForm = this.formBuilder.group({
      name: [],
      currentAge: [null, [Validators.min(18), Validators.max(70), Validators.pattern(/^[0-9]\d*$/)]],
      lifeExpectancy: [0],
      healthConditions: [[0]],
      retirementAge: [null, [Validators.min(30), Validators.max(75), Validators.pattern(/^[0-9]\d*$/)]],
      gender: ['', Validators.required],
      annualIncome: [null, [Validators.required, Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      retirementState: ['', Validators.required],
      maritalStatus: ['', Validators.required],
      spouseAnnualIncome: [0],
    }, { validator: this.ageValidator });

    this.preRetirementForm = this.formBuilder.group({
      outOfPocket: [0],
      adjustedValue: [0],
      modifiedValue: [0]
    })

    this.inRetirementForm = this.formBuilder.group({
      firstYearRetirementCosts: ['0']
    })

    this.clientHSADetailsForm = this.formBuilder.group({
      hsaBalance: [0, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      //ZSS 090421: these 7200 values need to NOT be static since they change every year unless they are reset else where, then make them like 10000000
      //hsaContribution: [0, [Validators.min(0), Validators.max(7200), Validators.pattern(/^[0-9]\d*$/)]],
      //ZSS 090521: these two values are based on sliders that set dynamic max values. Limits here have been removed. This is causing validation issues in the application.
      hsaContribution: [0],
      overrideOOPs: [0],
      employerHSAOption: [-1, [Validators.min(0), Validators.max(3), Validators.pattern(/^[0-9]\d*$/)]],
      employerHSAContribute: [0],
      preRetirementInvestment: [6, [Validators.required, Validators.min(0), Validators.max(8)]],
      retirementInvestment: [6, [Validators.required, Validators.min(0), Validators.max(8)]],
      matchRate: [0],
      matchLimit: [0],
    });

    this.resultsForm = this.formBuilder.group({
      annualHSAContribution: [0, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      retirementAge: [this.clientObjectForm.value.retirementAge, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      lifeExpectancy: [this.clientObjectForm.value.lifeExpectancy || 87, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      spouseRetirementAge: [this.spouseObjectForm.value.retirementAge, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      spouseLifeExpectancy: [this.spouseObjectForm.value.lifeExpectancy || 87, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]]
    });

    this.userObjectForm = this.formBuilder.group(
      {
        firstName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(30), Validators.pattern('^[a-zA-Z \'\-\.]+')]],
        currentAge: ['', [Validators.required, Validators.min(18), Validators.max(90)]],
        retirementAge:  ['', [Validators.required, Validators.min(18), Validators.max(90)]],
        isCurrentAgeMedicareAge: false
      });

  }

  resetFormData() {
    // Reset all forms
    this.requestObjectForm.reset();
    this.requestObjectForm.patchValue(this.requestObjectForm);
    this.requestObjectSource.next(this.requestObjectForm);

    this.clientObjectForm.reset();
    //this.clientObjectForm.value.healthConditions = [0];
    this.clientObjectForm.value.lifeExpectancy = 0;
    this.clientObjectForm.patchValue(this.clientObjectForm);
    this.clientObjectSource.next(this.clientObjectForm);

    this.clientObjectForm.value.spouseAnnualIncome = 0;
    this.clientObjectForm.patchValue({ healthConditions: [0] });
    
    this.spouseObjectForm.reset();
    //this.spouseObjectForm.value.healthConditions = [0];
    this.spouseObjectForm.value.lifeExpectancy = 0;
    this.spouseObjectForm.patchValue(this.spouseObjectForm);
    this.spouseObjectSource.next(this.spouseObjectForm);
    
    this.spouseObjectForm.patchValue({ healthConditions: [0] });

    this.familyObjectForm.reset();
    this.familyObjectForm.patchValue(this.familyObjectForm);
    this.familyObjectSource.next(this.familyObjectForm);

    this.preRetirementForm.reset();
    this.preRetirementForm.patchValue(this.preRetirementForm);
    this.preRetirementObjectSource.next(this.preRetirementForm);

    this.inRetirementForm.reset();
    this.inRetirementForm.patchValue(this.inRetirementForm);
    this.inRetirementObjectSource.next(this.inRetirementForm);

    
    this.clientHSADetailsForm = this.formBuilder.group({
      hsaBalance: [0, [Validators.min(0), Validators.max(10000000), Validators.pattern(/^[0-9]\d*$/)]],
      hsaContribution: [0],
      overrideOOPs: [0],
      employerHSAOption: [-1, [Validators.min(0), Validators.max(3), Validators.pattern(/^[0-9]\d*$/)]],
      employerHSAContribute: [0],
      preRetirementInvestment: [6, [Validators.required, Validators.min(0), Validators.max(8)]],
      retirementInvestment: [6, [Validators.required, Validators.min(0), Validators.max(8)]],
      matchRate: [0],
      matchLimit: [0],
    });

    this.clientHSADetailsForm.patchValue(this.clientHSADetailsForm);
    this.clientHSADetailsSource.next(this.clientHSADetailsForm);

    this.resultsResponse = {};

    this.depKids = [];

    // Globals in the global ts file
    this.global.defaultHCCCost = 0;
    this.global.originalOOPs = [];
    this.global.originalOverrideOOP = 0;
    this.global.isAllocateHC = false;
  }

  updateGetResultsResponse(results: any) {
    this.resultsResponse = results;
    const client = this.clientObjectForm.value as Client;

    const spouse = this.spouseObjectForm.value as Spouse;

    this.updateClientForm(client);
    this.updateSpouseObject(spouse);

    this.resultsResponseSource.next(this.resultsResponse);
  }

  updateRequestObject(requestObject: HSABankRequestObject) {
    this.requestObjectForm.patchValue(requestObject);
    this.requestObjectSource.next(this.requestObjectForm);
  }

  updateSpouseObject(spouse: Spouse) {
    this.spouseObjectForm.patchValue(spouse);
    this.spouseObjectSource.next(this.spouseObjectForm);
  }


  updateFamilyObject(family: Family, childrenUpdated?: Boolean) {
    this.familyObjectForm.patchValue(family);
    this.familyObjectSource.next(this.familyObjectForm);
    this.depChildObjectUpdate = true;
  }

  updateFinancialDetailsForm(outOfPocket) {
    this.preRetirementForm.patchValue({ 'outOfPocket': outOfPocket });
    this.preRetirementObjectSource.next(this.preRetirementForm);
  }

  rememberAdjustedValue(adjustedValue) {
    this.preRetirementForm.patchValue({ 'adjustedValue': adjustedValue });
    this.preRetirementObjectSource.next(this.preRetirementForm);
  }

  updateFinancialInRetirementDetailsForm(firstYearRetirementCosts) {
    this.inRetirementForm.patchValue({ 'firstYearRetirementCosts': firstYearRetirementCosts });
    this.inRetirementObjectSource.next(this.inRetirementForm);
  }

  updateHSADetailsForm(hsaDetails) {
    let employerHSAContributeMax = this.expenseObject.hsaLimit ? this.expenseObject.hsaLimit : 3600;
    hsaDetails.preRetirementInvestment = Number(hsaDetails.preRetirementInvestment);
    hsaDetails.retirementInvestment = Number(hsaDetails.retirementInvestment);

    if (hsaDetails.employerHSAOption == 1) {
      this.clientHSADetailsForm.get('matchRate').setValidators([Validators.min(1), Validators.max(100), Validators.pattern(/^[0-9]\d*$/)]);
      this.clientHSADetailsForm.get('matchLimit').setValidators([Validators.min(1), Validators.max(100), Validators.pattern(/^[0-9]\d*$/)]);
      this.clientHSADetailsForm.get('employerHSAContribute').setValidators(null);
    } else if (hsaDetails.employerHSAOption == 2 || hsaDetails.employerHSAOption == 3) {
      //the 8200 limit on  this validator needs to be updated every year if we leave this here...
      // this.clientHSADetailsForm.get('employerHSAContribute').setValidators([Validators.min(1), Validators.max(8200), Validators.pattern(/^[0-9]\d*$/)]);
      this.clientHSADetailsForm.get('employerHSAContribute').setValidators([Validators.min(1), Validators.max(employerHSAContributeMax), Validators.pattern(/^[0-9]\d*$/)]);
      this.clientHSADetailsForm.get('matchRate').setValidators(null);
      this.clientHSADetailsForm.get('matchLimit').setValidators(null);
      hsaDetails.matchRate = hsaDetails.matchLimit = 0;
    } else {
      this.clientHSADetailsForm.get('employerHSAContribute').setValidators(null);
      this.clientHSADetailsForm.get('matchRate').setValidators(null);
      this.clientHSADetailsForm.get('matchLimit').setValidators(null);
      hsaDetails.employerHSAContribute = hsaDetails.matchRate = hsaDetails.matchLimit = 0;
    }

    this.clientHSADetailsForm.get('matchRate').updateValueAndValidity();
    this.clientHSADetailsForm.get('matchLimit').updateValueAndValidity();
    this.clientHSADetailsForm.get('employerHSAContribute').updateValueAndValidity();
    this.clientHSADetailsForm.patchValue({ ...hsaDetails });
    this.clientHSADetailsSource.next(this.clientHSADetailsForm);
  }

  updateClientForm(client: Client) {
    this.clientObjectForm.patchValue(client);
    this.clientObjectSource.next(this.clientObjectForm);
  }

  mappingFromUserObject(client: Client) {
    client.currentAge = this.userObjectForm.value.currentAge;
    client.retirementAge = this.userObjectForm.value.retirementAge;
    client.name = this.userObjectForm.value.firstName;
    client.isCurrentAgeMedicareAge = this.userObjectForm.value.isCurrentAgeMedicareAge;
    return client;

  }

  updateResultsForm(annualHSAContribution) {
    this.resultsForm.patchValue(annualHSAContribution);
    this.resultsSource.next(annualHSAContribution);
  }
  setExpenseData(expenseData: any) {
    // console.log('expenseData');
    // console.log(expenseData.hsaLimit);
    this.expenseObject = expenseData;
    // console.log(this.expenseObject.hsaLimit);
  }

  private previousRetirementAge;
  private spousePreviousRetirementAge;
  private depChildObjectUpdate = true;
  private depKids = [];


  getRequestObject() {
    let currentDate = new Date();
    let currentYear = currentDate.getFullYear();
    let requestObject = {
      hasDownloadedPDF: this.hasDownloadedPDF,
      planForYear: this.requestObjectForm.value.planForYear,
      name: this.clientObjectForm.value.name,
      currentAge: this.clientObjectForm.value.currentAge,
      isCurrentAgeMedicareAge: this.userObjectForm.value.isCurrentAgeMedicareAge,
      userDetails: {
        people: [
          {
            currentAge: this.clientObjectForm.value.currentAge,
            gender: this.clientObjectForm.value.gender,
            healthConditions: this.clientObjectForm.value.healthConditions || [0],
            annualSalary: this.clientObjectForm.value.annualIncome,
            // annualSalary: this.clientObjectForm.value.maritalStatus == 2 ? this.clientObjectForm.value.spouseAnnualIncome + this.clientObjectForm.value.annualIncome : this.clientObjectForm.value.annualIncome,
            lifeExpectancy: this.clientObjectForm.value.lifeExpectancy,
            changedRetirementAge: this.clientObjectForm.value.retirementAge,
            retirementAge: this.clientObjectForm.value.retirementAge,
            retirementState: this.clientObjectForm.value.retirementState,
            maritalStatus: this.mapMaritalStatusForAPI(this.clientObjectForm.value.maritalStatus),
            totalHouseHoldIncome: this.mapMaritalStatusForAPI(this.clientObjectForm.value.maritalStatus) == 1 ? (this.clientObjectForm.value.spouseAnnualIncome + this.clientObjectForm.value.annualIncome): this.clientObjectForm.value.annualIncome,
          }
        ],
        dependents: {
          dependentsOnInsurancePlan: 0,
          dependentsRemovedFromPlanAtAge: 0,
          children: [],
          hasOverrideCosts: false
        }
      },
      financialDetails: {
        //retirementIncome: this.clientHSADetailsForm.value.hsaContribution,
        overrideOOPs: this.requestObjectForm.value.planForYear > currentYear ? Math.round(this.clientHSADetailsForm.value.overrideOOPs / (1.058)) : this.clientHSADetailsForm.value.overrideOOPs || 0,
        hsaBalance: this.clientHSADetailsForm.value.hsaBalance,
        hsaContrAmount: this.clientHSADetailsForm.value.hsaContribution,
        hasHSAContrAmount: this.clientHSADetailsForm.value.hsaContribution > 0 ? true : false,
        employerHSA: this.clientHSADetailsForm.value.employerHSAOption == 3 ? 2 : this.clientHSADetailsForm.value.employerHSAOption || -1,
        employerHSAMatchRate: this.clientHSADetailsForm.value.matchRate,
        employerHSAMatchLimit: this.clientHSADetailsForm.value.matchLimit,
        employerHSAContrAmount: this.clientHSADetailsForm.value.employerHSAContribute,
        returnOnInvestmentPreRetirement: this.clientHSADetailsForm.value.preRetirementInvestment || 6,
        returnOnInvestmentInRetirement: this.clientHSADetailsForm.value.retirementInvestment || 6,
      },
      org: this.global.orgID
    }

    if (this.spouseObjectForm.value && this.spouseObjectForm.value.currentAge) {
      requestObject.userDetails.people[1] = {
        changedRetirementAge: this.spouseObjectForm.value.retirementAge,
        currentAge: this.spouseObjectForm.value.currentAge,
        gender: this.spouseObjectForm.value.gender,
        healthConditions: this.spouseObjectForm.value.healthConditions || [0],
        lifeExpectancy: this.spouseObjectForm.value.lifeExpectancy,
        annualSalary: this.clientObjectForm.value.spouseAnnualIncome,
        retirementAge: this.spouseObjectForm.value.retirementAge,
        retirementState: this.clientObjectForm.value.retirementState,
      } as any;

      this.spousePreviousRetirementAge = this.spouseObjectForm.value.retirementAge;
    }

    if (this.familyObjectForm.value.dependentsCount > 0) {
      requestObject.userDetails.dependents = {
        dependentsOnInsurancePlan: this.familyObjectForm.value.dependentsCount,
        dependentsRemovedFromPlanAtAge: this.familyObjectForm.value.dependentsCutOffAge,
        children: this.depChildObjectUpdate ? [] : this.familyObjectForm.value.dependents,
        hasOverrideCosts: this.familyObjectForm.value.hasOverrideCosts
      } as any;

      if (this.familyObjectForm.value.dependents.length > 0) {
        this.familyObjectForm.value.dependents.forEach((child, idx) => {
          requestObject.userDetails.dependents.children[idx] = child;
        })
      }
    } else {
      requestObject.userDetails.dependents.children = this.depKids;
    }

    this.previousRetirementAge = this.clientObjectForm.value.retirementAge;

    if (this.depChildObjectUpdate) {
      requestObject.userDetails.dependents.children.forEach((child, idx) => {
        child.overrideOOPs = this.requestObjectForm.value.planForYear > currentYear ? Math.round(child.overrideOOPs / (1.058)) : child.overrideOOPs;
      })
    }
    
    if (this.depChildObjectUpdate) {
      this.depChildObjectUpdate = false;
      this.depKids = requestObject.userDetails.dependents.children;
    }

    if (this.familyObjectForm.value.dependentsCount == 0) {
      requestObject.userDetails.dependents.children = [];
      this.depKids = [];
    }

    return requestObject;
  }

  mapMaritalStatusForAPI(selectedValue) {
    let mapValue;
    if (selectedValue == 2) {
      mapValue = 1;
    } else {
      mapValue = 0;
    }
    return mapValue;
  }

  ageValidator(control: AbstractControl): ValidationErrors | null {
    const age = control.get("currentAge").value;
    const retirementAge = control.get("retirementAge").value;
    if (age >= retirementAge) { return { 'not equal': true } }

    return null
  }



}
